import { useRef, useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'
import {
  calculateLineItemTotal,
  getProductReceiptLines,
  type ProductReceiptLine,
} from 'src/shared/components/OrderTable/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { resolveQuoteItemVariantColor } from 'src/shared/utils/quoteItemThumbnail'
import { ProductPreview } from 'src/shared/components/ProductGrid/ProductCard/ProductPreview'
import { ColorVariantSelector } from 'src/shared/components/ProductGrid/ProductCard/ProductPreview/components/ColorVariantSelector'
import type { ProposalLine } from '../utils'
import {
  MAX_LINE_QUANTITY,
  MIN_LINE_QUANTITY,
  formatProposalMoney,
  formatProposalUnitPrice,
} from '../utils'
import { ProposalItemVisual } from './ProposalItemVisual'

/** Product, then per-unit decoration, then one-off setup — independent of row order in the database. */
const RECEIPT_ORDER: Record<ProductReceiptLine['kind'], number> = {
  product: 0,
  decoration: 1,
  setup_cost: 2,
  custom_line: 3,
  other: 4,
}

const optionName = (
  item: QuoteItem_,
  t: (en: string, sv?: string) => string
) => item.product?.item_name || item.description || t('Product', 'Produkt')

interface ProposalLineSectionProps {
  line: ProposalLine
  /** The chosen option, already priced at the line quantity. */
  chosen: QuoteItem_
  quantity: number
  currency: string
  locked: boolean
  onChoose: (optionId: number) => void
  /** Step the line quantity by +1 / -1 (applied to the latest value). */
  onQuantityStep: (delta: 1 | -1) => void
  /** The viewer may save a colour for the chosen option (before approval). */
  canChangeColour: boolean
  /** Saves the colour on the option; rejects when it could not be saved. */
  onColourChange: (optionId: number, colourId: number) => Promise<void>
}

export const ProposalLineSection = ({
  line,
  chosen,
  quantity,
  currency,
  locked,
  onChoose,
  onQuantityStep,
  canChangeColour,
  onColourChange,
}: ProposalLineSectionProps) => {
  const { t } = useLocalization()
  const { chosen_product_variant_color: chosenColour, product_variant_colors } =
    resolveQuoteItemVariantColor(chosen)
  const colorName = chosenColour?.color
  // The colour being saved; the swatch shows it until the proposal reloads.
  const [savingColourId, setSavingColourId] = useState<number | null>(null)
  const swatchesRef = useRef<HTMLDivElement>(null)
  const showColourPicker =
    !locked && canChangeColour && Boolean(chosenColour) && product_variant_colors.length > 1
  const pickColour = async (colourId: number) => {
    if (savingColourId !== null || colourId === chosenColour?.id) return
    setSavingColourId(colourId)
    try {
      await onColourChange(chosen.id, colourId)
    } catch {
      // The page reports the failure; the swatch goes back to the saved colour.
    } finally {
      setSavingColourId(null)
    }
  }
  const title = line.slot ?? optionName(chosen, t)
  const receipt = [...getProductReceiptLines(chosen, t)].sort(
    (x, y) => RECEIPT_ORDER[x.kind] - RECEIPT_ORDER[y.kind]
  )
  const hasAlternatives = line.options.length > 1
  // Product details open in the side panel; they never change the selection.
  const [detailsItem, setDetailsItem] = useState<QuoteItem_ | null>(null)

  return (
    <section className="flex flex-col gap-3.5 rounded-[20px] border border-[var(--raised-border)] bg-proposal-surface shadow-soft p-6">
      <Row className="flex-wrap items-baseline gap-2.5">
        <h2 className="m-0 text-base font-semibold tracking-[-0.01em] text-proposal-ink">
          {title}
        </h2>
        <span className="text-[13.5px] tabular-nums text-proposal-ink-muted">
          {[`${quantity} ${t('pcs', 'st')}`, colorName].filter(Boolean).join(' · ')}
        </span>
        <span className="ml-auto text-[17px] font-semibold tabular-nums text-proposal-ink">
          {formatProposalMoney(calculateLineItemTotal(chosen), currency)}
        </span>
      </Row>

      <div
        role={hasAlternatives ? 'radiogroup' : undefined}
        aria-label={hasAlternatives ? title : undefined}
        className="flex flex-wrap gap-3"
      >
        {line.options.map((option) => (
          <OptionCard
            key={option.id}
            option={option}
            name={optionName(option, t)}
            selected={option.id === chosen.id}
            selectable={hasAlternatives}
            locked={locked}
            currency={currency}
            onChoose={() => onChoose(option.id)}
            onViewDetails={() => setDetailsItem(option)}
          />
        ))}
      </div>

      {locked ? null : (
        <Row className="items-center justify-between gap-4 border-t border-proposal-ink/[0.09] pt-3">
          <span className="text-[14px] font-semibold text-proposal-ink">
            {t('How many?', 'Hur många?')}
          </span>
          <QuantityStepper
            label={title}
            quantity={quantity}
            onStep={onQuantityStep}
          />
        </Row>
      )}

      {showColourPicker && chosenColour ? (
        <Row className="flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-proposal-ink/[0.09] pt-3">
          <span className="text-[14px] font-semibold text-proposal-ink">
            {t('Colour', 'Färg')}
          </span>
          <div
            className={cn(
              'min-w-0 max-w-full',
              savingColourId !== null && 'pointer-events-none opacity-60'
            )}
            aria-busy={savingColourId !== null}
          >
            <ColorVariantSelector
              scrollContainerRef={swatchesRef}
              chosen_product_variant_color_={
                product_variant_colors.find((c) => c.id === savingColourId) ??
                chosenColour
              }
              product_variant_colors={product_variant_colors}
              setProduct_variant_color={(colour) => void pickColour(colour.id)}
            />
          </div>
        </Row>
      ) : null}

      <Column className="gap-[5px] border-t border-proposal-ink/[0.09] pt-3 text-[13.5px] text-proposal-ink-soft">
        {receipt.map((row) => (
          <Row key={row.key} className="justify-between gap-4">
            <span>
              {row.kind === 'product'
                ? `${row.quantity} × ${formatProposalUnitPrice(row.unitPrice, currency)}`
                : row.kind === 'setup_cost'
                  ? t('Setup, once', 'Uppstart, en gång')
                  : row.showDetail
                    ? `${t(row.label)}, ${row.quantity} × ${formatProposalUnitPrice(row.unitPrice, currency)}`
                    : t(row.label)}
            </span>
            <span className="shrink-0 tabular-nums">
              {formatProposalMoney(row.amount, currency)}
            </span>
          </Row>
        ))}
      </Column>

      {detailsItem ? (
        <ProposalProductDetails
          item={detailsItem}
          onClose={() => setDetailsItem(null)}
        />
      ) : null}
    </section>
  )
}

/** The catalogue side panel for one option, in proposal mode (set by the page). */
const ProposalProductDetails = ({
  item,
  onClose,
}: {
  item: QuoteItem_
  onClose: () => void
}) => {
  const product = item.product
  if (!product?.id) return null
  const { chosen_product_variant_color, product_variant_colors } =
    resolveQuoteItemVariantColor(item)
  if (!chosen_product_variant_color) return null

  return (
    <ProductPreview
      open
      setOpen={(open) => {
        if (!open) onClose()
      }}
      product_id={product.id}
      chosen_product_variant_color={chosen_product_variant_color}
      product_variant_colors={product_variant_colors}
      product_name={product.item_name || ''}
      product_description={product.item_description || ''}
      moq={product.moq || 1}
      article_number={product.article_number || ''}
      price={product.price || 0}
      klayder_recommends={product.klayder_recommends || false}
      klayder_recommendation_text={product.klayder_recommendation_text || ''}
      showAddToQuote={false}
    />
  )
}

const stepButtonClass =
  'grid size-7 place-items-center rounded-full border border-proposal-ink/15 bg-proposal-surface text-proposal-ink transition-colors hover:border-proposal-ink/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-proposal-orange disabled:cursor-not-allowed disabled:opacity-40'

const QuantityStepper = ({
  label,
  quantity,
  onStep,
}: {
  label: string
  quantity: number
  onStep: (delta: 1 | -1) => void
}) => {
  const { t } = useLocalization()
  const pcs = t('pcs', 'st')

  return (
    <Row className="items-center gap-1.5">
      <button
        type="button"
        className={stepButtonClass}
        aria-label={t(`Fewer ${label}`, `Färre ${label}`)}
        disabled={quantity <= MIN_LINE_QUANTITY}
        onClick={() => onStep(-1)}
      >
        <Minus className="size-3.5" aria-hidden />
      </button>
      <span
        className="min-w-[3.25rem] text-center tabular-nums text-proposal-ink"
        aria-live="polite"
      >
        {quantity} {pcs}
      </span>
      <button
        type="button"
        className={stepButtonClass}
        aria-label={t(`More ${label}`, `Fler ${label}`)}
        disabled={quantity >= MAX_LINE_QUANTITY}
        onClick={() => onStep(1)}
      >
        <Plus className="size-3.5" aria-hidden />
      </button>
    </Row>
  )
}

const OptionCard = ({
  option,
  name,
  selected,
  selectable,
  locked,
  currency,
  onChoose,
  onViewDetails,
}: {
  option: QuoteItem_
  name: string
  selected: boolean
  selectable: boolean
  locked: boolean
  currency: string
  onChoose: () => void
  onViewDetails: () => void
}) => {
  const { t } = useLocalization()
  const reason = option.notes?.trim()
  const interactive = selectable && !locked

  // The card frame holds two sibling buttons: choosing the option, and
  // opening its details (a button cannot sit inside another button).
  return (
    <div
      className={cn(
        'flex min-w-0 flex-[1_1_11rem] flex-col rounded-[14px] font-tiktok-sans transition-colors',
        // A line with one option keeps the card at alternative size, not full width.
        !selectable && 'max-w-[18rem]',
        selectable && selected
          ? 'border-[1.5px] border-proposal-orange bg-proposal-option-selected'
          : 'border border-proposal-ink/10 bg-proposal-option',
        interactive && !selected && 'hover:border-proposal-ink/25',
        locked && selectable && !selected && 'opacity-60'
      )}
    >
      <button
        type="button"
        role={selectable ? 'radio' : undefined}
        aria-checked={selectable ? selected : undefined}
        disabled={!interactive}
        onClick={onChoose}
        className={cn(
          'flex flex-1 flex-col gap-2.5 rounded-[13px] p-3.5 pb-2.5 text-left',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-proposal-orange focus-visible:ring-offset-2',
          interactive ? 'cursor-pointer' : 'cursor-default'
        )}
      >
        <ProposalItemVisual
          item={option}
          alt={name}
          className="aspect-[4/3] w-full rounded-[10px] bg-white"
        />
        <Row className="items-center gap-[7px]">
          {selectable ? (
            <span
              aria-hidden
              className={cn(
                'size-4 shrink-0 rounded-full border-[1.5px]',
                selected
                  ? 'border-proposal-orange bg-proposal-orange'
                  : 'border-proposal-ink/25 bg-transparent'
              )}
            />
          ) : null}
          <span className="text-[14.5px] font-semibold text-proposal-ink">
            {name}
          </span>
        </Row>
        {reason ? (
          <span className="text-[13px] leading-[1.45] text-proposal-ink-soft">
            {option.is_recommended && !/^(our pick|vårt val)/i.test(reason)
              ? `${t('Our pick.', 'Vårt val.')} ${reason}`
              : reason}
          </span>
        ) : option.is_recommended ? (
          <span className="text-[13px] leading-[1.45] text-proposal-ink-soft">
            {t('Our pick.', 'Vårt val.')}
          </span>
        ) : null}
        <span className="text-[13.5px] tabular-nums text-proposal-ink-body">
          {formatProposalUnitPrice(option.unit_price || 0, currency)} /{' '}
          {t('pc', 'st')}
        </span>
      </button>
      <button
        type="button"
        onClick={onViewDetails}
        aria-label={`${t('View details', 'Visa detaljer')}: ${name}`}
        className="mx-3.5 mb-3 self-start rounded-sm text-[13px] font-semibold text-proposal-orange-ink transition-colors hover:text-proposal-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-proposal-orange focus-visible:ring-offset-2"
      >
        {t('View details', 'Visa detaljer')}
      </button>
    </div>
  )
}
